import { useState } from "react";
import { Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { KlyrLogo } from "@/components/MarketingSite";

const inputCls = "w-full rounded-xl bg-navy-900/60 border border-gold-500/15 px-4 py-3 font-ui text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-gold-400/60 transition-colors";
const btnCls = "w-full flex items-center justify-center gap-2 font-ui font-semibold bg-gold-400 hover:bg-gold-300 text-stone-950 rounded-xl py-3 transition-colors disabled:opacity-60";

export const ForgotPasswordForm = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      setSent((await api.forgotPassword(email)).message);
    } catch (err) {
      setError(apiError(err, "Could not send the reset link."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
      <p className="font-read text-sm text-slate-400">Enter your account email and we'll send you a link to choose a new password.</p>
      <input data-testid="forgot-email-input" type="email" required placeholder="you@example.com" value={email}
        onChange={(e) => setEmail(e.target.value)} className={inputCls} />
      {sent && <p data-testid="forgot-sent" className="font-ui text-sm text-emerald-300">{sent}</p>}
      {error && <p data-testid="forgot-error" className="font-ui text-sm text-red-400">{error}</p>}
      <button data-testid="forgot-submit-button" disabled={busy || !!sent} className={btnCls}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Send reset link
      </button>
      <button type="button" data-testid="forgot-back-to-login" onClick={onBack}
        className="flex items-center gap-1 font-ui text-sm text-slate-400 hover:text-slate-200 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </button>
    </form>
  );
};

export default function ResetPasswordScreen({ onDone }) {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setError("Use at least 8 characters.");
    if (pw !== pw2) return setError("The two passwords don't match.");
    setBusy(true);
    setError("");
    try {
      await api.resetPassword(token, pw);
      setDone(true);
    } catch (err) {
      setError(apiError(err, "Could not reset your password."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6" data-testid="reset-password-screen">
      <div className="w-full max-w-md space-y-6">
        <a href="/" data-testid="reset-home-link" className="flex items-center gap-3 font-ui font-semibold tracking-[0.2em] text-gold-100">
          <KlyrLogo className="w-10 h-7" /> KLYR STUDIO
        </a>
        <h1 data-testid="reset-heading" className="font-display text-4xl text-slate-100">{done ? "Password updated" : "Choose a new password"}</h1>
        {done ? (
          <div className="space-y-4">
            <p data-testid="reset-success" className="font-read text-slate-300">Your password has been changed. Other devices have been signed out.</p>
            <button data-testid="reset-go-signin" onClick={onDone} className={btnCls}>Sign in</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="reset-password-input" type="password" placeholder="New password (8+ characters)" value={pw}
              onChange={(e) => setPw(e.target.value)} className={inputCls} />
            <input data-testid="reset-password-confirm-input" type="password" placeholder="Repeat new password" value={pw2}
              onChange={(e) => setPw2(e.target.value)} className={inputCls} />
            {error && <p data-testid="reset-error" className="font-ui text-sm text-red-400">{error}</p>}
            <button data-testid="reset-submit-button" disabled={busy || !token} className={btnCls}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Save new password
            </button>
            {!token && <p className="font-ui text-xs text-slate-500">This link is missing its code. Request a new one from the sign-in page.</p>}
          </form>
        )}
      </div>
    </div>
  );
}
